import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useOrganization } from "@/hooks/use-organization";
import { useTRPC } from "@/trpc";
import { useOrganizationWageSetMutationHandlers } from "../hooks/use-organization-wage-set-mutations";
import { getTodayInTimezone, isCalendarDate } from "../utils/wage-dates";
import { DateField } from "./date-field";
import { OrganizationWageSetDialog } from "./organization-wage-set-dialog";

type RetireOrganizationWageSetDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationWageSetId: string;
  organizationWageSetName: string;
  currentVersionId: string;
};

export function RetireOrganizationWageSetDialog({
  open,
  onOpenChange,
  organizationWageSetId,
  organizationWageSetName,
  currentVersionId,
}: RetireOrganizationWageSetDialogProps) {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const { invalidateOrganizationWageSets, handleError } =
    useOrganizationWageSetMutationHandlers();
  const [lastDay, setLastDay] = useState(() =>
    getTodayInTimezone({ timezoneId: selectedOrgBusinessUnit.timezoneId }),
  );
  const [dateError, setDateError] = useState<string>();

  const retireMutation = useMutation(
    trpc.organizationWageSet.retire.mutationOptions({
      onSuccess: async () => {
        await invalidateOrganizationWageSets();
        toast.success("Wage set retired");
        onOpenChange(false);
      },
      onError: handleError,
    }),
  );

  function handleSubmit() {
    if (!isCalendarDate(lastDay)) {
      setDateError("Choose a last day");
      return;
    }
    setDateError(undefined);
    retireMutation.mutate({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      organizationWageSetId,
      lastDay,
      expectedCurrentVersionId: currentVersionId,
    });
  }

  return (
    <OrganizationWageSetDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Retire ${organizationWageSetName}`}
      description="The wage set stops applying after its last day. Its history and past pay stay on record."
      submitLabel="Retire wage set"
      isPending={retireMutation.isPending}
      onSubmit={handleSubmit}
    >
      <DateField
        label="Last day"
        value={lastDay}
        onChange={setLastDay}
        error={dateError}
      />
    </OrganizationWageSetDialog>
  );
}
